import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Eye, EyeOff, Info, KeyRound, Mail } from "lucide-react";
import { Button, ErrorAlert, Input } from "../../../components/ui";
import { MolduraDeAcesso } from "../components/MolduraDeAcesso";
import { problemaDaSenhaNova, SENHA_MAXIMO } from "../../conta/regrasDeSenha";
import {
  lerErro,
  recuperacaoIndisponivel,
  usePedirCodigo,
  useRedefinirSenha,
  useValidarCodigo,
  type ContaRecuperavel,
  type DesafioDeRecuperacao,
  type ErroDeRecuperacao,
} from "./api";

type Etapa =
  | { tipo: "email"; email: string }
  | { tipo: "codigo"; email: string; desafio: string; reenviarEm: number }
  | { tipo: "senha"; email: string; desafio: string; codigo: string; contas: ContaRecuperavel[] }
  | { tipo: "pronto"; login: string }
  | { tipo: "indisponivel" };

const BOTAO_PRINCIPAL =
  "w-full bg-gradient-to-r from-blue-800 to-blue-600 text-white font-medium py-3 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200";

const FORMATO_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const quandoReenviar = (desafio: DesafioDeRecuperacao) =>
  Date.now() + desafio.reenviarEmSegundos * 1000;

/**
 * Esqueci a senha, em três passos: e-mail, código e senha nova.
 *
 * A tela nunca diz se o e-mail tem conta, porque a API também não diz: o
 * passo do código fala "se houver uma conta", e um código para e-mail sem
 * conta só não confere. Se a recuperação não estiver ligada no ambiente
 * (e-mail desligado ou escrita fechada), aparece o caminho que existia antes:
 * falar com a T4i.
 */
const RecuperarSenha = () => {
  const [etapa, setEtapa] = useState<Etapa>({ tipo: "email", email: "" });

  switch (etapa.tipo) {
    case "email":
      return (
        <MolduraDeAcesso titulo="Recuperar senha" subtitulo="Enviamos um código para o e-mail cadastrado">
          <EtapaEmail
            emailInicial={etapa.email}
            aoEnviar={(email, desafio) =>
              setEtapa({ tipo: "codigo", email, desafio: desafio.desafio, reenviarEm: quandoReenviar(desafio) })
            }
            aoIndisponivel={() => setEtapa({ tipo: "indisponivel" })}
          />
        </MolduraDeAcesso>
      );

    case "codigo":
      return (
        <MolduraDeAcesso titulo="Digite o código">
          <EtapaCodigo
            email={etapa.email}
            desafio={etapa.desafio}
            reenviarEm={etapa.reenviarEm}
            aoValidar={(codigo, contas) =>
              setEtapa({ tipo: "senha", email: etapa.email, desafio: etapa.desafio, codigo, contas })
            }
            aoReenviar={(desafio) =>
              setEtapa({ ...etapa, desafio: desafio.desafio, reenviarEm: quandoReenviar(desafio) })
            }
            aoTrocarEmail={() => setEtapa({ tipo: "email", email: etapa.email })}
            aoIndisponivel={() => setEtapa({ tipo: "indisponivel" })}
          />
        </MolduraDeAcesso>
      );

    case "senha":
      return (
        <MolduraDeAcesso titulo="Crie uma senha nova">
          <EtapaSenha
            desafio={etapa.desafio}
            codigo={etapa.codigo}
            contas={etapa.contas}
            aoConcluir={(login) => setEtapa({ tipo: "pronto", login })}
            aoRecomecar={() => setEtapa({ tipo: "email", email: etapa.email })}
          />
        </MolduraDeAcesso>
      );

    case "pronto":
      return (
        <MolduraDeAcesso titulo="Senha alterada">
          <EtapaPronto login={etapa.login} />
        </MolduraDeAcesso>
      );

    case "indisponivel":
      return (
        <MolduraDeAcesso titulo="Recuperar senha" subtitulo="Como voltar a acessar o portal">
          <Indisponivel />
        </MolduraDeAcesso>
      );
  }
};

// ------------------------------------------------------------------ e-mail

const EtapaEmail = ({
  emailInicial,
  aoEnviar,
  aoIndisponivel,
}: {
  emailInicial: string;
  aoEnviar: (email: string, desafio: DesafioDeRecuperacao) => void;
  aoIndisponivel: () => void;
}) => {
  const pedir = usePedirCodigo();
  const [email, setEmail] = useState(emailInicial);
  const [erro, setErro] = useState<ErroDeRecuperacao | null>(null);

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setErro(null);

    const limpo = email.trim();
    if (!FORMATO_EMAIL.test(limpo)) {
      setErro({ titulo: "E-mail incompleto", detalhe: "Informe o e-mail no formato nome@empresa.com.br." });
      return;
    }

    try {
      aoEnviar(limpo, await pedir.mutateAsync(limpo));
    } catch (e) {
      const lido = lerErro(e);
      if (recuperacaoIndisponivel(lido)) aoIndisponivel();
      else setErro(lido);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-5" noValidate>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          E-mail
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nome@empresa.com.br"
          maxLength={254}
        />
      </div>

      <Button type="submit" className={BOTAO_PRINCIPAL} loading={pedir.isPending}>
        {pedir.isPending ? "Enviando…" : "Enviar código"}
      </Button>

      {erro && (
        <ErrorAlert
          title={erro.titulo ?? "Não foi possível enviar"}
          message={erro.detalhe}
          onClose={() => setErro(null)}
          variant={erro.status === 429 ? "warning" : "error"}
        />
      )}

      <p className="flex items-start gap-2 text-sm text-gray-600">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <span>
          Se você ainda consegue entrar, dá para trocar a senha por dentro do portal, em{" "}
          <strong>Trocar senha</strong>, no topo da tela.
        </span>
      </p>

      <div className="text-center">
        <Link to="/login" className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-500">
          Voltar ao login
        </Link>
      </div>
    </form>
  );
};

// ------------------------------------------------------------------ código

/**
 * Contagem até poder pedir outro código, e depois o botão. Montada com a chave
 * do prazo: cada código novo recomeça a contagem do zero, sem herdar relógio.
 */
const Reenvio = ({
  reenviarEm,
  enviando,
  aoReenviar,
}: {
  reenviarEm: number;
  enviando: boolean;
  aoReenviar: () => void;
}) => {
  const [agora, setAgora] = useState(() => Date.now());
  const faltam = Math.max(0, Math.ceil((reenviarEm - agora) / 1000));

  useEffect(() => {
    if (faltam === 0) return;
    const tique = window.setTimeout(() => setAgora(Date.now()), 1000);
    return () => window.clearTimeout(tique);
  }, [agora, faltam]);

  return faltam > 0 ? (
    <span className="tabular-nums text-gray-500">
      Reenviar em {Math.floor(faltam / 60)}:{String(faltam % 60).padStart(2, "0")}
    </span>
  ) : (
    <button
      type="button"
      onClick={aoReenviar}
      disabled={enviando}
      className="font-medium text-blue-600 transition-colors hover:text-blue-500 disabled:opacity-50"
    >
      {enviando ? "Enviando…" : "Reenviar código"}
    </button>
  );
};

const EtapaCodigo = ({
  email,
  desafio,
  reenviarEm,
  aoValidar,
  aoReenviar,
  aoTrocarEmail,
  aoIndisponivel,
}: {
  email: string;
  desafio: string;
  reenviarEm: number;
  aoValidar: (codigo: string, contas: ContaRecuperavel[]) => void;
  aoReenviar: (desafio: DesafioDeRecuperacao) => void;
  aoTrocarEmail: () => void;
  aoIndisponivel: () => void;
}) => {
  const validar = useValidarCodigo();
  const pedir = usePedirCodigo();
  const [digitos, setDigitos] = useState("");
  const [erro, setErro] = useState<ErroDeRecuperacao | null>(null);
  const [esgotado, setEsgotado] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  const conferir = async (codigo: string) => {
    if (validar.isPending) return;
    setErro(null);

    try {
      aoValidar(codigo, await validar.mutateAsync({ desafio, codigo }));
    } catch (e) {
      const lido = lerErro(e);
      // Tentativas acabaram, ou o código venceu: só um código novo resolve.
      const perdido =
        lido.status === 429 || lido.titulo === "Código expirado" || lido.titulo === "Código já usado";
      setEsgotado(perdido);
      setErro(lido);
      // Código errado: o campo esvazia e volta o foco, para digitar de novo sem apagar à mão.
      if (!perdido) {
        setDigitos("");
        campo.current?.focus();
      }
    }
  };

  const aoDigitar = (valor: string) => {
    const limpo = valor.replace(/\D/g, "").slice(0, 6);
    setDigitos(limpo);
    // O sexto dígito já envia: é o que a pessoa faria em seguida.
    if (limpo.length === 6 && !esgotado) void conferir(limpo);
  };

  const reenviar = async () => {
    setErro(null);
    try {
      aoReenviar(await pedir.mutateAsync(email));
      // Código novo, desafio novo: o que foi digitado e as tentativas ficam para trás.
      setDigitos("");
      setEsgotado(false);
      setReenviado(true);
    } catch (e) {
      const lido = lerErro(e);
      if (recuperacaoIndisponivel(lido)) aoIndisponivel();
      else setErro(lido);
    }
  };

  return (
    <form
      onSubmit={(evento) => {
        evento.preventDefault();
        if (digitos.length === 6) void conferir(digitos);
      }}
      className="space-y-5"
      noValidate
    >
      <p className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/60 p-4 text-sm text-gray-700">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
        <span>
          {reenviado ? "Mandamos um código novo. " : ""}
          Se houver uma conta com <strong className="break-words">{email}</strong>, o código chega em
          instantes e vale por 15 minutos. Confira também a caixa de spam.
        </span>
      </p>

      <div>
        <label htmlFor="codigo" className="block text-sm font-medium text-gray-700 mb-2">
          Código de 6 dígitos{reenviado ? ", do e-mail mais recente" : ""}
        </label>
        <Input
          ref={campo}
          id="codigo"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={digitos.length > 3 ? `${digitos.slice(0, 3)} ${digitos.slice(3)}` : digitos}
          onChange={(e) => aoDigitar(e.target.value)}
          disabled={esgotado}
          placeholder="000 000"
          maxLength={7}
          aria-describedby={erro ? "codigo-erro" : undefined}
          className="h-14 text-center font-mono text-2xl tracking-[0.3em] placeholder:text-gray-300"
        />
      </div>

      {!esgotado && (
        <Button
          type="submit"
          className={BOTAO_PRINCIPAL}
          loading={validar.isPending}
          disabled={digitos.length !== 6}
        >
          {validar.isPending ? "Conferindo…" : "Continuar"}
        </Button>
      )}

      {erro && (
        <div id="codigo-erro">
          <ErrorAlert
            title={erro.titulo ?? "Não foi possível conferir"}
            message={erro.detalhe}
            onClose={esgotado ? undefined : () => setErro(null)}
            variant={esgotado ? "warning" : "error"}
          />
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={aoTrocarEmail}
          className="inline-flex items-center gap-1 text-gray-600 transition-colors hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Outro e-mail
        </button>

        <Reenvio key={reenviarEm} reenviarEm={reenviarEm} enviando={pedir.isPending} aoReenviar={reenviar} />
      </div>
    </form>
  );
};

// ------------------------------------------------------------------- senha

const EtapaSenha = ({
  desafio,
  codigo,
  contas,
  aoConcluir,
  aoRecomecar,
}: {
  desafio: string;
  codigo: string;
  contas: ContaRecuperavel[];
  aoConcluir: (login: string) => void;
  aoRecomecar: () => void;
}) => {
  const redefinir = useRedefinirSenha();
  const [idUsuario, setIdUsuario] = useState<number | null>(contas.length === 1 ? contas[0].id : null);
  const [nova, setNova] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState<ErroDeRecuperacao | null>(null);

  const problema =
    idUsuario === null
      ? "Escolha qual acesso vai ter a senha nova."
      : (problemaDaSenhaNova(nova) ??
        (confirmacao !== nova ? "A confirmação não confere com a senha nova." : null));

  // Cobra só depois de a pessoa chegar na confirmação ou tentar salvar.
  const mostrarProblema = tentou || confirmacao.length > 0;

  const codigoPerdido =
    erro !== null &&
    (erro.status === 429 || erro.titulo === "Código expirado" || erro.titulo === "Código já usado");

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setTentou(true);
    setErro(null);
    if (problema || idUsuario === null) return;

    try {
      aoConcluir(await redefinir.mutateAsync({ desafio, codigo, idUsuario, novaSenha: nova }));
    } catch (e) {
      setErro(lerErro(e));
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-5" noValidate>
      {contas.length > 1 ? (
        <fieldset>
          <legend className="mb-2 block text-sm font-medium text-gray-700">
            Este e-mail tem {contas.length} acessos. Qual vai ter a senha nova?
          </legend>
          <div className="space-y-2">
            {contas.map((conta) => (
              <label
                key={conta.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                  idUsuario === conta.id
                    ? "border-blue-500 bg-blue-50/60"
                    : "border-gray-200 bg-white/60 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="conta"
                  checked={idUsuario === conta.id}
                  onChange={() => setIdUsuario(conta.id)}
                  className="h-4 w-4 accent-blue-700"
                />
                <span className="min-w-0">
                  <span className="font-medium text-gray-800 break-words">{conta.login}</span>
                  {conta.cliente && <span className="text-gray-500"> · {conta.cliente}</span>}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : (
        <p className="text-sm text-gray-600">
          Acesso: <strong className="text-gray-800 break-words">{contas[0]?.login}</strong>
          {contas[0]?.cliente && <span> · {contas[0].cliente}</span>}
        </p>
      )}

      <div className="space-y-3">
        <CampoSenha
          id="senha-nova"
          rotulo="Senha nova"
          valor={nova}
          aoMudar={setNova}
          mostrar={mostrar}
          aoAlternar={() => setMostrar((v) => !v)}
          autoFocus={contas.length === 1}
        />
        <CampoSenha
          id="senha-confirmacao"
          rotulo="Repita a senha nova"
          valor={confirmacao}
          aoMudar={setConfirmacao}
          mostrar={mostrar}
        />
        <p className="text-xs text-gray-500">
          Ao menos 6 caracteres. Letras sem acento, números e símbolos do teclado.
        </p>
        {problema && mostrarProblema && <p className="text-sm text-amber-700">{problema}</p>}
      </div>

      <Button type="submit" className={BOTAO_PRINCIPAL} loading={redefinir.isPending}>
        {redefinir.isPending ? "Salvando…" : "Salvar senha nova"}
      </Button>

      {erro && (
        <div className="space-y-2">
          <ErrorAlert
            title={erro.titulo ?? "Não foi possível salvar"}
            message={erro.detalhe}
            onClose={codigoPerdido ? undefined : () => setErro(null)}
            variant={codigoPerdido ? "warning" : "error"}
          />
          {codigoPerdido && (
            <button
              type="button"
              onClick={aoRecomecar}
              className="w-full text-center text-sm font-medium text-blue-600 transition-colors hover:text-blue-500"
            >
              Pedir um código novo
            </button>
          )}
        </div>
      )}
    </form>
  );
};

const CampoSenha = ({
  id,
  rotulo,
  valor,
  aoMudar,
  mostrar,
  aoAlternar,
  autoFocus,
}: {
  id: string;
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  mostrar: boolean;
  aoAlternar?: () => void;
  autoFocus?: boolean;
}) => (
  <div>
    <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
      {rotulo}
    </label>
    <div className="relative">
      <Input
        id={id}
        type={mostrar ? "text" : "password"}
        value={valor}
        autoFocus={autoFocus}
        autoComplete="new-password"
        onChange={(e) => aoMudar(e.target.value)}
        maxLength={SENHA_MAXIMO}
        className={aoAlternar ? "pr-10" : undefined}
      />
      {/* Um olho só, no primeiro campo, vale para os dois. */}
      {aoAlternar && (
        <button
          type="button"
          onClick={aoAlternar}
          aria-label={mostrar ? "Ocultar senhas" : "Mostrar senhas"}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-gray-600"
        >
          {mostrar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  </div>
);

// ------------------------------------------------------------------- fim

const EtapaPronto = ({ login }: { login: string }) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
        <Check className="h-6 w-6 text-emerald-600" />
      </div>
      <p className="text-gray-700">
        A senha nova já vale. Seu usuário para entrar é{" "}
        <strong className="text-gray-900 break-words">{login}</strong>.
      </p>
      <Button
        className={BOTAO_PRINCIPAL}
        autoFocus
        onClick={() => navigate("/login", { state: { login, senhaRedefinida: true } })}
      >
        Ir para o login
      </Button>
    </div>
  );
};

const Indisponivel = () => (
  <div className="space-y-5">
    <p className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/60 p-4 text-sm text-gray-700">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
      <span>
        A recuperação de senha por e-mail não está disponível agora. Para voltar a acessar, fale com a
        equipe da T4i: ela redefine o seu acesso.
      </span>
    </p>

    <p className="flex items-start gap-2 text-sm text-gray-600">
      <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <span>
        Se você ainda consegue entrar, dá para trocar a senha por dentro do portal, em{" "}
        <strong>Trocar senha</strong>, no topo da tela.
      </span>
    </p>

    <div className="text-center pt-2">
      <Link to="/login" className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-500">
        Voltar ao login
      </Link>
    </div>
  </div>
);

export default RecuperarSenha;
